import React from 'react';

import type EventEmitter from 'eventemitter3';

import type { CxEventEmitter, CxEvents } from '/imports/ui/components/CxEventEmitter';

/**
 *  Wraps `addEventListener` and ensures they get removed correctly.
 */
export function useEventListener(): (typeof window)['addEventListener'];
export function useEventListener<T extends EventTarget>(target: T): T['addEventListener'];
// eslint-disable-next-line @eslint-react/no-unnecessary-use-prefix
export function useEventListener(target: EventTarget = window) {
  return function useEventListenerOnTarget(eventName: string, handler: EventListener) {
    const { current } = React.useRef({
      handler,
      target,
      staticHandler: (event: Event) => void current.handler(event),
    });
    current.handler = handler;
    current.target = target;
    React.useEffect(() => {
      current.target.addEventListener(eventName, current.staticHandler);
      return () => {
        current.target.removeEventListener(eventName, current.staticHandler);
      };
    }, [current.target, eventName, current.staticHandler]);
  };
}

export function useEventListener3<T extends EventEmitter.EventNames<CxEvents>>(
  eventName: T,
  handler: EventEmitter.EventListener<CxEvents, T>,
  target: CxEventEmitter,
): void {
  // Create a ref that stores handler
  const savedHandler = React.useRef<EventEmitter.EventListener<CxEvents, T>>(undefined);

  // Update ref.current value if handler changes.
  // This allows our effect below to always get latest handler ...
  // ... without us needing to pass it in effect deps array ...
  // ... and potentially cause effect to re-run every render.
  React.useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  React.useEffect(
    () => {
      const eventListener: EventEmitter.EventListener<CxEvents, T> = (event) => {
        if (savedHandler.current) {
          savedHandler.current(event);
        }
      };

      // Add event listener
      target.on(eventName, eventListener);
      return () => {
        target.off(eventName, eventListener);
      };
    },
    [eventName, target], // Re-run if eventName or element changes
  );
}
